import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { z } from "zod";

import { Column, ErrorText, Row } from "@/components";
import { AutosaveFormEnumSelect } from "@/components/companies/shared/autosave-form/AutosaveFormEnumSelect";
import { AutosaveFormNumberInput } from "@/components/companies/shared/autosave-form/AutosaveFormNumberInput";
import { AutosaveFormRichTextInput } from "@/components/companies/shared/autosave-form/AutosaveFormRichTextInput";
import { IntakeBoolean } from "@/components/companies/shared/IntakeBoolean";
import { IntakeForm } from "@/components/companies/shared/IntakeForm";
import { ReadOnlyBooleanField, ReadOnlyField, ReadOnlyRichTextField } from "@/components/companies/shared/ReadOnlyField";
import { Label } from "@/components/ui/label";

import { useUpdateSetCompanyIntake } from "@/hooks/updateSets/useUpdateSetCompanyIntake";
import { staffingFirmCostStructureOptions } from "@/lib/constants";
import { nullableNonOptionalNumberSchema } from "@/lib/schemas";
import { mixpanelUtils } from "@/lib/mixpanel/mixpanel";
import { MIXPANEL_SCREENS } from "@/lib/mixpanel/types";
import { CompanyIntake, StaffingFirmCostStructure } from "@/types";

const talentRecruitingHistorySchema = z.object({
  general: z.object({
    retention: z.string().nullable(),
    hiringChallenges: z.string().nullable(),
    competitors: z.string().nullable(),
    currentlyUsingStaffingFirms: z.boolean().nullable().optional(),
    historicallyUsedStaffingFirms: z.boolean().nullable().optional(),
    details: z.string().nullable(),
  }),
  internal: z.object({
    hrTeamSize: nullableNonOptionalNumberSchema,
    currentHrBandwidthOnRecruiting: nullableNonOptionalNumberSchema,
    idealHrBandwidthOnRecruiting: nullableNonOptionalNumberSchema,
    details: z.string().nullable(),
  }),
  external: z.object({
    details: z.string().nullable(),
    staffingFirmCostStructure: z
      .nativeEnum(StaffingFirmCostStructure)
      .nullable(),
    staffingFirmCostDetails: z.string().nullable(),
    likesAboutExternalRecruiters: z.string().nullable(),
    dislikesAboutExternalRecruiters: z.string().nullable(),
  }),
});

export const TalentRecruitingHistory = ({
  companyIntake,
  isEditing,
}: {
  companyIntake: CompanyIntake;
  isEditing: boolean;
}) => {
  const { mutate: updateCompanyIntake, isError } = useUpdateSetCompanyIntake();
  const history = companyIntake.talentRecruitingHistory;

  const form = useForm<z.infer<typeof talentRecruitingHistorySchema>>({
    resolver: zodResolver(talentRecruitingHistorySchema),
    defaultValues: history,
  });

  const currentlyUsingStaffingFirms = form.watch(
    "general.currentlyUsingStaffingFirms",
  );
  const historicallyUsedStaffingFirms = form.watch(
    "general.historicallyUsedStaffingFirms",
  );

  const handleSave = (data: z.infer<typeof talentRecruitingHistorySchema>) => {
    mixpanelUtils.trackFormSubmission("Talent recruiting history", MIXPANEL_SCREENS.COMPANY);
    updateCompanyIntake({
      updateCompanyIntakeRequest: { talentRecruitingHistory: data },
    });
  };

  const saveOnBlur = () => {
    void form.handleSubmit(handleSave)();
  };

  const costStructureLabel =
    staffingFirmCostStructureOptions.find(
      (o) => o.value === history.external.staffingFirmCostStructure,
    )?.label ?? null;

  return (
    <Column className="gap-6">
      {isEditing ? (
        <IntakeForm form={form} onSave={saveOnBlur}>
          <Column className="gap-6">
            <Column className="gap-12">
              {/* General */}
              <Column className="gap-4">
                <Label className="text-md font-semibold text-slate-700">
                  General
                </Label>

                <Row className="gap-4">
                  <IntakeBoolean
                    checked={currentlyUsingStaffingFirms}
                    className="flex-1"
                    label="Currently Using Staffing Firms"
                    onToggle={() => {
                      form.setValue(
                        "general.currentlyUsingStaffingFirms",
                        !currentlyUsingStaffingFirms,
                      );
                      saveOnBlur();
                    }}
                  />
                  <IntakeBoolean
                    checked={historicallyUsedStaffingFirms}
                    className="flex-1"
                    label="Historically Used Staffing Firms"
                    onToggle={() => {
                      form.setValue(
                        "general.historicallyUsedStaffingFirms",
                        !historicallyUsedStaffingFirms,
                      );
                      saveOnBlur();
                    }}
                  />
                </Row>

                <AutosaveFormRichTextInput
                  editorHeight="150px"
                  fieldName="general.retention"
                  form={form}
                  infoTooltip={
                    "What have your retention rates been like historically across the various roles at your company?\nDo you have any insight as to why turnover might be happening?"
                  }
                  label="Retention"
                  onBlur={saveOnBlur}
                />

                <AutosaveFormRichTextInput
                  editorHeight="150px"
                  fieldName="general.hiringChallenges"
                  form={form}
                  infoTooltip={
                    "What have been your struggles with hiring? Is it internal bandwidth, low quality or slow external recruiting, all of the above?\nWhat are some specific misses that come to mind?"
                  }
                  label="Hiring Challenges"
                  onBlur={saveOnBlur}
                />

                <AutosaveFormRichTextInput
                  editorHeight="150px"
                  fieldName="general.competitors"
                  form={form}
                  infoTooltip={
                    "Who are your main competitors?\nHave you had success hiring employees who have worked at these companies before?"
                  }
                  label="Competitors"
                  onBlur={saveOnBlur}
                />

                <AutosaveFormRichTextInput
                  editorHeight="150px"
                  fieldName="general.details"
                  form={form}
                  infoTooltip="Any other notable details about this client's general talent and recruiting history that don't fit in the fields above."
                  label="Details"
                  onBlur={saveOnBlur}
                />
              </Column>

              {/* Internal Recruiting History */}
              <Column className="gap-4">
                <Label className="text-md font-semibold text-slate-700">
                  Internal Recruiting History
                </Label>

                <AutosaveFormNumberInput
                  fieldName="internal.hrTeamSize"
                  form={form}
                  label="HR Team Size"
                  onBlur={saveOnBlur}
                  placeholder="e.g., 5"
                />

                <Row className="gap-4">
                  <AutosaveFormNumberInput
                    className="flex-1"
                    fieldName="internal.currentHrBandwidthOnRecruiting"
                    form={form}
                    label="Current HR Bandwidth on Recruiting (%)"
                    maxLength={3}
                    onBlur={saveOnBlur}
                    placeholder="e.g., 60"
                  />
                  <AutosaveFormNumberInput
                    className="flex-1"
                    fieldName="internal.idealHrBandwidthOnRecruiting"
                    form={form}
                    label="Ideal HR Bandwidth on Recruiting (%)"
                    maxLength={3}
                    onBlur={saveOnBlur}
                    placeholder="e.g., 30"
                  />
                </Row>

                <AutosaveFormRichTextInput
                  editorHeight="150px"
                  fieldName="internal.details"
                  form={form}
                  infoTooltip={
                    "Is recruiting taking up more of the HR and hiring team's time than you would like?\nWhat would your ideal process look like internally if Laborup could support by providing a speedy and high quality funnel of candidates?"
                  }
                  label="Commentary on Internal Hiring"
                  onBlur={saveOnBlur}
                />
              </Column>

              {/* External Recruiting History */}
              <Column className="gap-4">
                <Label className="text-md font-semibold text-slate-700">
                  External Recruiting History
                </Label>

                <AutosaveFormEnumSelect
                  fieldName="external.staffingFirmCostStructure"
                  form={form}
                  infoTooltip="Have companies traditionally charged you fixed or percentage based rates?"
                  label="Staffing Firm Cost Structure"
                  onSelect={saveOnBlur}
                  options={staffingFirmCostStructureOptions}
                />

                <AutosaveFormRichTextInput
                  editorHeight="150px"
                  fieldName="external.staffingFirmCostDetails"
                  form={form}
                  infoTooltip={
                    "How have staffing firms charged this client in the past?\nHas the cost structure looked different for different roles?\nWhat were the typical rates or markups?"
                  }
                  label="Staffing Firm Cost Details"
                  onBlur={saveOnBlur}
                />

                <AutosaveFormRichTextInput
                  editorHeight="150px"
                  fieldName="external.likesAboutExternalRecruiters"
                  form={form}
                  infoTooltip={
                    "What does this company appreciate about working with external recruiters?\nWhat has gone well in past partnerships?\nWhat qualities or practices do they value most?"
                  }
                  label="Likes About External Recruiters"
                  onBlur={saveOnBlur}
                />

                <AutosaveFormRichTextInput
                  editorHeight="150px"
                  fieldName="external.dislikesAboutExternalRecruiters"
                  form={form}
                  infoTooltip={
                    "What frustrations has this company had with external recruiters?\nWhat are their biggest pain points or deal-breakers?\nWhat practices or behaviors do they want to avoid?"
                  }
                  label="Dislikes About External Recruiters"
                  onBlur={saveOnBlur}
                />

                <AutosaveFormRichTextInput
                  editorHeight="150px"
                  fieldName="external.details"
                  form={form}
                  infoTooltip={
                    "What has been your experience with staffing agencies in the past?\nWho have you worked with?\nWhat did you like about working with that firm?\nWhat did you dislike about working with that firm?"
                  }
                  label="Details"
                  onBlur={saveOnBlur}
                />
              </Column>
            </Column>

            {isError && (
              <ErrorText errorMessage="An error occurred while saving. Please try again." />
            )}
          </Column>
        </IntakeForm>
      ) : (
        <Column className="gap-12">
          {/* General */}
          <Column className="gap-4">
            <Label className="text-md font-semibold text-slate-700">
              General
            </Label>
            <Row className="gap-4">
              <ReadOnlyBooleanField
                className="flex-1"
                label="Currently Using Staffing Firms"
                isSelected={history.general.currentlyUsingStaffingFirms}
              />
              <ReadOnlyBooleanField
                className="flex-1"
                label="Historically Used Staffing Firms"
                isSelected={history.general.historicallyUsedStaffingFirms}
              />
              <div className="flex-1" />
            </Row>
            <ReadOnlyRichTextField
              label="Retention"
              value={history.general.retention}
            />
            <ReadOnlyRichTextField
              label="Hiring Challenges"
              value={history.general.hiringChallenges}
            />
            <ReadOnlyRichTextField
              label="Competitors"
              value={history.general.competitors}
            />
            <ReadOnlyRichTextField
              label="Details"
              value={history.general.details}
            />
          </Column>

          {/* Internal Recruiting History */}
          <Column className="gap-4">
            <Label className="text-md font-semibold text-slate-700">
              Internal Recruiting History
            </Label>
            <ReadOnlyField
              label="HR Team Size"
              value={
                history.internal.hrTeamSize != null
                  ? String(history.internal.hrTeamSize)
                  : null
              }
            />
            <Row className="gap-4">
              <ReadOnlyField
                className="flex-1"
                label="Current HR Bandwidth on Recruiting (%)"
                value={
                  history.internal.currentHrBandwidthOnRecruiting != null
                    ? String(history.internal.currentHrBandwidthOnRecruiting)
                    : null
                }
              />
              <ReadOnlyField
                className="flex-1"
                label="Ideal HR Bandwidth on Recruiting (%)"
                value={
                  history.internal.idealHrBandwidthOnRecruiting != null
                    ? String(history.internal.idealHrBandwidthOnRecruiting)
                    : null
                }
              />
            </Row>
            <ReadOnlyRichTextField
              label="Commentary on Internal Hiring"
              value={history.internal.details}
            />
          </Column>

          {/* External Recruiting History */}
          <Column className="gap-4">
            <Label className="text-md font-semibold text-slate-700">
              External Recruiting History
            </Label>
            <ReadOnlyField
              label="Staffing Firm Cost Structure"
              value={costStructureLabel}
            />
            <ReadOnlyRichTextField
              label="Staffing Firm Cost Details"
              value={history.external.staffingFirmCostDetails}
            />
            <ReadOnlyRichTextField
              label="Likes About External Recruiters"
              value={history.external.likesAboutExternalRecruiters}
            />
            <ReadOnlyRichTextField
              label="Dislikes About External Recruiters"
              value={history.external.dislikesAboutExternalRecruiters}
            />
            <ReadOnlyRichTextField
              label="Details"
              value={history.external.details}
            />
          </Column>
        </Column>
      )}
    </Column>
  );
};
